import { api } from './api.js';

/**
 * Browser notifications (Web Push). The service worker at /sw.js shows them; this module turns
 * them on and off for the current browser and keeps the server's copy of the subscription fresh.
 */
export type PushState = 'unsupported' | 'blocked' | 'off' | 'on';

export class PushPermissionError extends Error {
  constructor(readonly permission: NotificationPermission) {
    super(`Notification permission ${permission}`);
  }
}

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  return registration ? registration.pushManager.getSubscription() : null;
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  return Notification.permission === 'granted' && (await currentSubscription()) ? 'on' : 'off';
}

const toKeyBytes = (base64url: string) => {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64url.length / 4) * 4, '=');
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
};

const sameKey = (subscription: PushSubscription, publicKey: string) => {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;
  const a = new Uint8Array(current);
  const b = toKeyBytes(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
};

/** "Chrome on Windows" and similar, so people can tell their browsers apart. */
export function deviceLabel(userAgent = navigator.userAgent): string {
  const browser = /Edg\//.test(userAgent) ? 'Edge' : /OPR\//.test(userAgent) ? 'Opera' : /Firefox\//.test(userAgent) ? 'Firefox' : /Chrome\//.test(userAgent) ? 'Chrome' : /Safari\//.test(userAgent) ? 'Safari' : 'Browser';
  const os = /Windows/.test(userAgent) ? 'Windows' : /Android/.test(userAgent) ? 'Android' : /iPhone|iPad/.test(userAgent) ? 'iOS' : /Mac OS X/.test(userAgent) ? 'macOS' : /Linux/.test(userAgent) ? 'Linux' : '';
  return os ? `${browser} · ${os}` : browser;
}

async function register(subscription: PushSubscription) {
  const json = subscription.toJSON();
  await api.call('registerPushSubscription', { body: { endpoint: subscription.endpoint, keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' }, label: deviceLabel() } });
}

/** Asks for permission (from a click), subscribes this browser and registers it with the server. */
export async function enablePush(publicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushPermissionError(permission);
  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !sameKey(subscription, publicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKeyBytes(publicKey) });
  await register(subscription);
}

/** Stops notifications in this browser. */
export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription();
  if (!subscription) return;
  await api.call('unregisterPushSubscription', { body: { endpoint: subscription.endpoint } }).catch(() => undefined);
  await subscription.unsubscribe();
}

/**
 * On sign-in: if this browser already shows notifications, re-register it (idempotent). This moves
 * a shared browser to whoever signed in and recovers from a rotated server key.
 */
export async function syncPush(publicKey: string | null): Promise<void> {
  if (!publicKey || !pushSupported() || Notification.permission !== 'granted') return;
  const subscription = await currentSubscription();
  if (!subscription) return;
  if (!sameKey(subscription, publicKey)) {
    await subscription.unsubscribe();
    await enablePush(publicKey);
    return;
  }
  await register(subscription);
}
